import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requireFeature } from '@/common/utils/requireFeature';
import { techSystemsService } from '@/features/tech-systems/services/tech-systems.service';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';
import { useState } from 'react';
import { TechSystemsListFilters } from '@/features/tech-systems/components/TechSystemsListFilters';
import { TechSystemsGrid } from '@/features/tech-systems/components/TechSystemsGrid';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_authenticated/tech-systems/')({
  beforeLoad: () => requireFeature('TECH_SYSTEMS'),
  component: TechSystemsListPage,
});

function TechSystemsListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.TECH_SYSTEMS_MANAGE);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [systemType, setSystemType] = useState('platform');
  const [isInternal, setIsInternal] = useState(true);

  const { data: systems = [], isLoading } = useQuery({
    queryKey: ['tech-systems', search, statusFilter],
    queryFn: () =>
      techSystemsService.list({
        ...(search && { search }),
        status: statusFilter,
      }),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      techSystemsService.create({ name, systemType, isInternal }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['tech-systems'] });
      setCreateOpen(false);
      setName('');
      toast({ title: 'Sistema criado' });
      navigate({ to: '/tech-systems/$id', params: { id: created.id }, search: { tab: 'overview' } });
    },
  });

  return (
    <PermissionGate permission={Permission.TECH_SYSTEMS_VIEW}>
      <div className="space-y-6 p-6">
        <PageHeader
          title="Gestão de sistemas"
          description="Sistemas TEC que você tem acesso"
          actions={
            canManage && (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Novo sistema
              </Button>
            )
          }
        />

        <TechSystemsListFilters
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />

        {isLoading ? (
          <p className="text-muted-foreground">Carregando...</p>
        ) : systems.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              Nenhum sistema encontrado. Solicite acesso a um gestor ou crie um
              novo sistema.
            </CardContent>
          </Card>
        ) : (
          <TechSystemsGrid
            systems={systems}
            onSelect={(system) =>
              navigate({ to: '/tech-systems/$id', params: { id: system.id }, search: { tab: 'overview' } })
            }
          />
        )}

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Novo sistema TEC</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={systemType} onValueChange={setSystemType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="platform">Plataforma</SelectItem>
                    <SelectItem value="website">Site</SelectItem>
                    <SelectItem value="hosting">Hospedagem</SelectItem>
                    <SelectItem value="server">Servidor</SelectItem>
                    <SelectItem value="service">Serviço</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  id="internal"
                />
                <Label htmlFor="internal">Sistema interno</Label>
              </div>
              <Button
                className="w-full"
                disabled={!name.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                Criar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PermissionGate>
  );
}
